import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Trophy, Gift, Lock, CheckCircle, Sparkles, Target, Flame, BookOpen, Compass } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import BottomNav from '../components/BottomNav';
import api from '../services/api';
import { toast } from '../hooks/use-toast';

const ChallengesAndRewards = () => {
  const navigate = useNavigate();
  const [challenges, setChallenges] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [weekInfo, setWeekInfo] = useState({});
  const [userBadges, setUserBadges] = useState(0);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('challenges');
  const [selectedReward, setSelectedReward] = useState(null);

  useEffect(() => {
    fetchData();
    // Track section visit for explorer challenge
    trackSectionVisit('challenges');
  }, []);

  const fetchData = async () => {
    try {
      const [challengesRes, rewardsRes] = await Promise.all([
        api.get('/challenges/weekly'),
        api.get('/rewards')
      ]);

      setChallenges(challengesRes.data.challenges || []);
      setWeekInfo({
        start: challengesRes.data.weekStart,
        end: challengesRes.data.weekEnd,
        totalPoints: challengesRes.data.totalPointsEarned,
        maxPoints: challengesRes.data.totalPointsPossible
      });

      setRewards(rewardsRes.data.rewards || []);
      setUserBadges(rewardsRes.data.userBadges || 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les données',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const trackSectionVisit = async (section) => {
    try {
      await api.post('/challenges/track-section', { section });
    } catch (e) {
      console.log('Track section failed:', e);
    }
  };

  const claimChallenge = async (challengeId) => {
    try {
      const response = await api.post(`/challenges/claim/${challengeId}`);
      toast({
        title: 'Bravo!',
        description: `Tu as gagné ${response.data.pointsEarned} points!`
      });
      fetchData(); // Refresh data
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de réclamer la récompense',
        variant: 'destructive'
      });
    }
  };

  const viewRewardContent = async (rewardId) => {
    try {
      const response = await api.get(`/rewards/${rewardId}`);
      setSelectedReward(response.data.reward);
    } catch (error) {
      toast({
        title: 'Verrouillé',
        description: error.response?.data?.detail || 'Cette récompense n\'est pas encore débloquée',
        variant: 'destructive'
      });
    }
  };

  const getChallengeIcon = (type) => {
    switch (type) {
      case 'lessons': return <BookOpen className="w-6 h-6" />;
      case 'quiz_passed': return <Target className="w-6 h-6" />;
      case 'checkins': return <Flame className="w-6 h-6" />;
      case 'sections_visited': return <Compass className="w-6 h-6" />;
      default: return <Trophy className="w-6 h-6" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-rose-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-pink-800 to-rose-900 pb-24">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-purple-900/95 to-pink-900/95 backdrop-blur-md p-4 flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white">
          <ArrowLeft className="w-6 h-6" />
        </Button>
        <h1 className="text-xl font-bold text-white flex items-center gap-2">
          <Trophy className="w-6 h-6 text-yellow-400" />
          Défis & Récompenses
        </h1>
      </div>

      {/* Tabs */}
      <div className="px-4 pt-4">
        <div className="flex bg-white/10 rounded-lg p-1">
          <button
            onClick={() => setActiveTab('challenges')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              activeTab === 'challenges'
                ? 'bg-pink-500 text-white'
                : 'text-white/70 hover:text-white'
            }`}
          >
            <Target className="w-4 h-4 inline mr-2" />
            Défis
          </button>
          <button
            onClick={() => setActiveTab('rewards')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
              activeTab === 'rewards'
                ? 'bg-pink-500 text-white'
                : 'text-white/70 hover:text-white'
            }`}
          >
            <Gift className="w-4 h-4 inline mr-2" />
            Récompenses
          </button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {activeTab === 'challenges' && (
          <>
            {/* Week Info */}
            <Card className="p-4 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border-yellow-400/30">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-yellow-400" />
                  Semaine du {formatDate(weekInfo.start)} au {formatDate(weekInfo.end)}
                </h3>
                <span className="text-yellow-300 font-bold">
                  {weekInfo.totalPoints}/{weekInfo.maxPoints} pts
                </span>
              </div>
              <Progress 
                value={(weekInfo.totalPoints / weekInfo.maxPoints) * 100} 
                className="h-2 bg-white/20"
              />
            </Card>

            {/* Challenges List */}
            {challenges.map((challenge) => (
              <Card 
                key={challenge.id} 
                className={`p-4 ${
                  challenge.completed 
                    ? 'bg-green-500/20 border-green-400/30' 
                    : 'bg-white/10 border-white/20'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full ${
                    challenge.completed ? 'bg-green-500' : 'bg-pink-500/30'
                  }`}>
                    <span className="text-2xl">{challenge.icon}</span>
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className="text-white font-bold">{challenge.title}</h4>
                      <span className="text-yellow-300 text-sm font-bold">
                        +{challenge.points} pts
                      </span>
                    </div>
                    <p className="text-pink-200 text-sm mb-3">{challenge.description}</p>
                    
                    <div className="flex items-center gap-3">
                      <Progress 
                        value={challenge.percentage} 
                        className="flex-1 h-2 bg-white/20"
                      />
                      <span className="text-white text-sm font-medium min-w-[50px] text-right">
                        {challenge.progress}/{challenge.target}
                      </span>
                    </div>

                    {challenge.percentage >= 100 && !challenge.completed && (
                      <Button 
                        onClick={() => claimChallenge(challenge.id)}
                        className="mt-3 w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                      >
                        <Trophy className="w-4 h-4 mr-2" />
                        Réclamer la récompense
                      </Button>
                    )}

                    {challenge.completed && (
                      <div className="mt-3 flex items-center gap-2 text-green-400">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Complété!</span>
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </>
        )}

        {activeTab === 'rewards' && (
          <>
            {/* Badge Progress */}
            <Card className="p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 border-purple-400/30">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-white font-bold">Tes badges</h3>
                <span className="text-pink-300 font-bold">{userBadges}/20</span>
              </div>
              <Progress value={(userBadges / 20) * 100} className="h-2 bg-white/20" />
              <p className="text-pink-200 text-sm mt-2">
                Gagne des badges pour débloquer des récompenses!
              </p>
            </Card>

            {/* Rewards List */}
            {rewards.map((reward) => (
              <Card 
                key={reward.id} 
                className={`p-4 ${
                  reward.unlocked 
                    ? 'bg-gradient-to-r from-yellow-500/20 to-amber-500/20 border-yellow-400/30' 
                    : 'bg-white/5 border-white/10'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-full relative ${
                    reward.unlocked ? 'bg-yellow-500' : 'bg-gray-500/30'
                  }`}>
                    <span className="text-2xl">{reward.icon}</span>
                    {!reward.unlocked && (
                      <Lock className="w-4 h-4 absolute -bottom-1 -right-1 text-gray-400" />
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex justify-between items-start mb-1">
                      <h4 className={`font-bold ${reward.unlocked ? 'text-white' : 'text-gray-400'}`}>
                        {reward.name}
                      </h4>
                      <span className={`text-sm font-bold ${
                        reward.unlocked ? 'text-green-400' : 'text-pink-300'
                      }`}>
                        {reward.userBadges}/{reward.badgesRequired} badges
                      </span>
                    </div>
                    <p className={`text-sm mb-3 ${reward.unlocked ? 'text-pink-200' : 'text-gray-500'}`}>
                      {reward.description}
                    </p>
                    
                    {!reward.unlocked && (
                      <div className="mb-3">
                        <Progress 
                          value={reward.progress} 
                          className="h-2 bg-white/10"
                        />
                        <p className="text-gray-400 text-xs mt-1">
                          Plus que {reward.badgesRequired - reward.userBadges} badge(s) à gagner!
                        </p>
                      </div>
                    )}

                    {reward.unlocked && (
                      <Button 
                        onClick={() => viewRewardContent(reward.id)}
                        className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
                      >
                        <Gift className="w-4 h-4 mr-2" />
                        Voir le contenu
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
          </>
        )}
      </div>

      {/* Reward Content Modal */}
      {selectedReward && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="max-w-md w-full p-6 bg-gradient-to-br from-purple-900 to-pink-900 border-yellow-400/30 max-h-[80vh] overflow-y-auto">
            <div className="text-center mb-4">
              <span className="text-5xl">{selectedReward.icon}</span>
              <h3 className="text-xl font-bold text-white mt-2">{selectedReward.name}</h3>
            </div>

            {selectedReward.contentType === 'tips' && (
              <div className="space-y-3">
                <h4 className="text-pink-300 font-semibold">Astuces exclusives:</h4>
                {selectedReward.content.map((tip, index) => (
                  <div key={`tip-${index}-${tip.slice(0,15)}`} className="p-3 bg-white/10 rounded-lg">
                    <p className="text-white text-sm">
                      <span className="text-yellow-400 mr-2">💡</span>
                      {tip}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {selectedReward.contentType === 'guide' && (
              <div className="space-y-3">
                <h4 className="text-pink-300 font-semibold">{selectedReward.content.title}</h4>
                {selectedReward.content.sections.map((section, index) => (
                  <div key={`section-${index}-${section.slice(0,15)}`} className="p-3 bg-white/10 rounded-lg flex items-center gap-2">
                    <span className="text-green-400">✓</span>
                    <p className="text-white text-sm">{section}</p>
                  </div>
                ))}
              </div>
            )}

            {selectedReward.contentType === 'badge' && (
              <div className="text-center">
                <div className="p-4 bg-yellow-500/20 rounded-lg inline-block mb-3">
                  <span className="text-4xl">🏅</span>
                </div>
                <p className="text-white font-bold">{selectedReward.content.title}</p>
                <p className="text-pink-200 text-sm mt-1">
                  Tu as débloqué un titre honorifique spécial!
                </p>
              </div>
            )}

            {selectedReward.contentType === 'vip' && (
              <div className="space-y-3">
                <h4 className="text-pink-300 font-semibold">Avantages VIP:</h4>
                {selectedReward.content.features.map((feature, index) => (
                  <div key={`feat-${index}-${feature.slice(0,15)}`} className="p-3 bg-white/10 rounded-lg flex items-center gap-2">
                    <span className="text-yellow-400">👑</span>
                    <p className="text-white text-sm">{feature}</p>
                  </div>
                ))}
              </div>
            )}

            <Button 
              onClick={() => setSelectedReward(null)}
              className="w-full mt-6 bg-white/20 hover:bg-white/30"
            >
              Fermer
            </Button>
          </Card>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default ChallengesAndRewards;
